import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  ChatHomeTilesApiClient,
  type ChatHomeTileCmsTile,
  type ChatHomeTilesConfig,
  type ChatHomeTilesScope,
} from "../cms/chat-home-tiles-api.js";
import { getChatHomeTilesErrorMessage } from "../cms/chat-home-tiles-errors.js";
import type { ChatHomeTile } from "../home-tiles.js";
import { HelpLightElement } from "./help-light-element.js";
import { HelpJonasHomeTileDialog } from "./jonas-home-tile-dialog.js";
import "./jonas-home-prompts.js";

@customElement("help-jonas-home-tile-manager")
export class HelpJonasHomeTileManager extends HelpLightElement {
  @property({ attribute: "config-id" })
  accessor configId = "chat-home-tiles-cms-config";

  @property({ attribute: "anti-forgery-selector" })
  accessor antiForgerySelector = "input[name='__RequestVerificationToken']";

  @property()
  accessor mode = "overlay";

  @state()
  private accessor config: ChatHomeTilesConfig | null = null;

  @state()
  private accessor currentScope: ChatHomeTilesScope | null = null;

  @state()
  private accessor isOpen = false;

  @state()
  private accessor isLoading = false;

  @state()
  private accessor errorMessage = "";

  @state()
  private accessor tiles: ChatHomeTileCmsTile[] = [];

  @state()
  private accessor previewTiles: ChatHomeTile[] = [];

  private api = new ChatHomeTilesApiClient();

  private tilesAbortController: AbortController | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.config = this.readConfig();
    this.currentScope = this.config?.initialScope ?? null;
    if (this.currentScope) {
      this.updateScopeUrl();
      void this.loadTiles();
    }
  }

  override disconnectedCallback(): void {
    this.tilesAbortController?.abort();
    super.disconnectedCallback();
  }

  protected override render() {
    if (this.mode === "overlay" && !this.isOpen) {
      return this.renderOverlay();
    }

    return this.renderManager();
  }

  private renderOverlay() {
    return html`<div
      class="cms-home-tiles-edit-overlay"
      data-chat-home-tiles-edit-overlay
    >
      <afas-button
        label="Tegels bewerken"
        data-action="open-tile-manager"
        @click=${() => {
          this.isOpen = true;
        }}
      >
        <afas-icon slot="icon" icon="EditFilled"></afas-icon>
      </afas-button>
    </div>`;
  }

  private renderManager() {
    const scope = this.currentScope;
    if (!this.config || !scope) return nothing;

    return html`<section class="cms-home-tiles" data-chat-home-tiles-manager>
      <header class="cms-home-tiles__toolbar">
        ${this.renderScopeSelect(
          "chat-home-country",
          "Land",
          this.getCountryOptions(),
          scope.country,
          (value) => this.setScopeValue("country", value),
        )}
        ${this.renderScopeSelect(
          "chat-home-product",
          "Product",
          this.getProductOptions(scope.country),
          scope.product,
          (value) => this.setScopeValue("product", value),
        )}
        ${this.renderScopeSelect(
          "chat-home-culture",
          "Taal",
          this.getCultureOptions(scope.country, scope.product),
          scope.culture,
          (value) => this.setScopeValue("culture", value),
        )}
        <afas-button
          label="Tegel toevoegen"
          data-action="create-tile"
          @click=${this.openCreateDialog}
        >
          <afas-icon slot="icon" icon="AddOutlined"></afas-icon>
        </afas-button>
      </header>

      ${this.errorMessage
        ? html`<afas-alert type="danger">${this.errorMessage}</afas-alert>`
        : null}
      <afas-alert type="info" data-preview-notice>
        Je bekijkt een preview van de opgeslagen chat home tegels.
      </afas-alert>

      <div class="cms-home-tiles__layout" ?aria-busy=${this.isLoading}>
        <section class="cms-home-tiles__management" aria-label="Tegels beheren">
          <table class="cms-home-tiles__table">
            <thead>
              <tr>
                <th>Titel</th>
                <th>Status</th>
                <th>Sortering</th>
                <th>Plaatsingen</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              ${this.tiles.map((tile) => this.renderTileRow(tile))}
            </tbody>
          </table>
        </section>

        <aside class="cms-home-tiles__preview" aria-label="Preview">
          <help-jonas-home-prompts
            .tiles=${this.previewTiles}
          ></help-jonas-home-prompts>
        </aside>
      </div>

      <help-jonas-home-tile-dialog
        .config=${this.config}
        .tiles=${this.tiles}
        .currentScope=${scope}
        .maxPublicTiles=${this.config.maxPublicTiles}
        .antiForgeryToken=${this.getAntiForgeryToken()}
        @chat-home-tile-saved=${this.handleTileMutation}
        @chat-home-tile-deleted=${this.handleTileMutation}
        @chat-home-tile-stale=${this.handleTileStale}
      ></help-jonas-home-tile-dialog>
    </section>`;
  }

  private renderTileRow(tile: ChatHomeTileCmsTile) {
    const scope = this.currentScope;
    const currentPlacement = tile.placements.find(
      (placement) =>
        placement.country === scope?.country &&
        placement.product === scope?.product,
    );
    const isActiveForCurrentScope = currentPlacement?.isActive ?? false;
    const sorting = tile.placements[0]?.sorting ?? 0;

    return html`<tr
      data-tile-id=${tile.id}
      data-active=${isActiveForCurrentScope}
    >
      <td>${tile.title}</td>
      <td>
        <afas-tag
          readonly
          color=${isActiveForCurrentScope
            ? "var(--color-positive)"
            : "var(--color-foreground-500)"}
          .value=${isActiveForCurrentScope ? "Actief" : "Inactief"}
        ></afas-tag>
      </td>
      <td>${sorting}</td>
      <td>${this.getPlacementLabels(tile)}</td>
      <td>
        <afas-button
          icon-only
          label="Wijzigen"
          data-action="edit-tile"
          @click=${() => this.openEditDialog(tile)}
        >
          <afas-icon slot="icon" icon="EditFilled"></afas-icon>
        </afas-button>
        <afas-button
          icon-only
          label="Verwijderen"
          data-action="delete-tile"
          @click=${() => this.openDeleteDialog(tile)}
        >
          <afas-icon slot="icon" icon="DeleteOutlined"></afas-icon>
        </afas-button>
      </td>
    </tr>`;
  }

  private renderScopeSelect(
    id: string,
    label: string,
    options: Array<{ value: string; label: string }>,
    value: string,
    onChange: (value: string) => void,
  ) {
    return html`<afas-field-row compact no-post-icon orientation="horizontal">
      <afas-label slot="label" for=${id}>${label}</afas-label>
      <afas-select
        id=${id}
        slot="value"
        .value=${value}
        .clearable=${false}
        .displayOptions=${["label"]}
        ?disabled=${this.isLoading}
        @change=${(event: CustomEvent<{ value?: string }>) =>
          onChange(event.detail?.value ?? value)}
      >
        ${options.map(
          (option) =>
            html`<afas-menu-item value=${option.value} label=${option.label}
              >${option.label}</afas-menu-item
            >`,
        )}
      </afas-select>
    </afas-field-row>`;
  }

  private async loadTiles(): Promise<void> {
    if (!this.currentScope) return;

    this.tilesAbortController?.abort();
    const abortController = new AbortController();
    this.tilesAbortController = abortController;
    this.isLoading = true;
    this.errorMessage = "";

    try {
      const response = await this.api.loadTiles(
        this.currentScope,
        abortController.signal,
      );
      if (this.tilesAbortController !== abortController) return;
      this.tiles = response.tiles ?? [];
      this.previewTiles = response.previewTiles ?? [];
    } catch (error) {
      if (!abortController.signal.aborted) {
        this.errorMessage = getChatHomeTilesErrorMessage(
          error,
          "Tegels konden niet worden geladen.",
        );
      }
    } finally {
      if (this.tilesAbortController === abortController) {
        this.isLoading = false;
      }
    }
  }

  private setScopeValue(
    key: "country" | "product" | "culture",
    value: string,
  ): void {
    if (!this.config || !this.currentScope || !value) return;

    const candidate = {
      country: key === "country" ? value : this.currentScope.country,
      product: key === "product" ? value : this.currentScope.product,
      culture: key === "culture" ? value : this.currentScope.culture,
    };
    const nextScope = this.findScope(candidate);
    if (!nextScope) return;

    this.currentScope = nextScope;
    this.updateScopeUrl();
    void this.loadTiles();
  }

  private findScope(candidate: {
    country: string;
    product: string;
    culture: string;
  }): ChatHomeTilesScope | null {
    return (
      this.config?.scopes.find(
        (scope) =>
          scope.country === candidate.country &&
          scope.product === candidate.product &&
          scope.culture === candidate.culture,
      ) ??
      this.config?.scopes.find(
        (scope) =>
          scope.country === candidate.country &&
          scope.product === candidate.product,
      ) ??
      this.config?.initialScope ??
      null
    );
  }

  private updateScopeUrl(): void {
    if (!this.currentScope) return;

    const url = new URL(window.location.toString());
    url.searchParams.set("country", this.currentScope.country);
    url.searchParams.set("product", this.currentScope.product);
    url.searchParams.set("culture", this.currentScope.culture);
    history.replaceState(history.state, "", url);
  }

  private readConfig(): ChatHomeTilesConfig | null {
    const script = document.getElementById(this.configId);
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent) as ChatHomeTilesConfig;
    } catch {
      return null;
    }
  }

  private getCountryOptions(): Array<{ value: string; label: string }> {
    const options = new Map<string, string>();
    for (const scope of this.config?.scopes ?? []) {
      options.set(scope.country, scope.helpCenterLabel);
    }
    return [...options].map(([value, label]) => ({ value, label }));
  }

  private getProductOptions(
    country: string,
  ): Array<{ value: string; label: string }> {
    const options = new Map<string, string>();
    for (const scope of this.config?.scopes ?? []) {
      if (scope.country === country)
        options.set(scope.product, scope.productLabel);
    }
    return [...options].map(([value, label]) => ({ value, label }));
  }

  private getCultureOptions(
    country: string,
    product: string,
  ): Array<{ value: string; label: string }> {
    return (this.config?.scopes ?? [])
      .filter((scope) => scope.country === country && scope.product === product)
      .map((scope) => ({ value: scope.culture, label: scope.cultureLabel }));
  }

  private getPlacementLabels(tile: ChatHomeTileCmsTile): string {
    return tile.placements
      .map((placement) => {
        const option = this.config?.placements.find(
          (candidate) =>
            candidate.country === placement.country &&
            candidate.product === placement.product,
        );
        return option
          ? `${option.helpCenterLabel} / ${option.productLabel}`
          : `${placement.country}/${placement.product}`;
      })
      .join(", ");
  }

  private getAntiForgeryToken(): string {
    const element = document.querySelector<HTMLInputElement>(
      this.antiForgerySelector,
    );
    return element?.value ?? "";
  }

  private get tileDialog(): HelpJonasHomeTileDialog | null {
    return this.querySelector("help-jonas-home-tile-dialog");
  }

  private openCreateDialog = (): void => {
    this.tileDialog?.openForCreate();
  };

  private openEditDialog(tile: ChatHomeTileCmsTile): void {
    this.tileDialog?.openForEdit(tile);
  }

  private openDeleteDialog(tile: ChatHomeTileCmsTile): void {
    this.tileDialog?.openForDelete(tile);
  }

  private handleTileMutation = (): void => {
    void this.loadTiles();
  };

  // The dialog detected a server-side concurrency conflict (HTTP 409). Refresh the
  // in-memory tiles so the editor's "reload latest" and client-side stale check work
  // against the newest version; the dialog stays open so the editor keeps their draft.
  private handleTileStale = (): void => {
    void this.loadTiles();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-home-tile-manager": HelpJonasHomeTileManager;
  }
}
